import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { SCButton, SCInput, SCSelect } from 'rollup-allspark';
import ComponentPlaceholder from '../../../../components/ComponentPlaceholder';
import {
  PRINTER_ROUTING_TAB_CATEGORY,
  PRINTER_ROUTING_TAB_ITEM,
  PRINTER_ROUTING_TABS,
} from '../../../../config/constants/printerProfiles';
import { cn, isEmpty, printf } from '../../../../utils';
import { getList, saveEntity, updateEntity } from '../../actions';
import { SAVE_BRAND_PROFILE_ROUTING } from '../../sagaMap';
import { makeSelectProperty } from '../../selector';
import CategoryList from './CategoryList';
import ItemList from './ItemList';

const AdvancedSelector = ({
  categories,
  checkedCategories,
  checkedItems,
  isSaving,
  items,
  itemSearchKey,
  menus,
  saveBrandProfileRouting,
  selectedBrand,
  selectedFilterCategory,
  selectedFilterMenu,
  selectedProfile,
  updateField,
}) => {
  const [selectedTab, setSelectedTab] = useState(PRINTER_ROUTING_TAB_CATEGORY);

  const onSaveClicked = () => {
    const filteredItems = checkedItems.filter(ci => {
      const menuItem = items.find(i => i.id === ci);
      const matchedLinkedCat = !isEmpty(menuItem.linkedCategories)
        ? menuItem.linkedCategories.find(lc =>
            checkedCategories.includes(lc.categoryID),
          )
        : null;

      return isEmpty(matchedLinkedCat);
    });

    const data = {
      brand_id: selectedBrand.id,
      categories: checkedCategories,
      items: filteredItems,
    };

    saveBrandProfileRouting({
      ...SAVE_BRAND_PROFILE_ROUTING,
      url: printf(SAVE_BRAND_PROFILE_ROUTING.url, selectedProfile.id),
      data,
    });
  };

  const onTabClicked = tab => {
    updateField({
      itemSearchKey: '',
      selectedFilterMenu: null,
      selectedFilterCategory: null,
    });

    setSelectedTab(tab);
  };

  const menuDD = !isEmpty(menus) ? (
    <SCSelect
      title="Filter by Menu"
      options={menus}
      trackBy="sectionID"
      customLabel={m => `${m.sectionID}. ${m.title.en}`}
      placeholder="Select Menu"
      className="w-1/3"
      value={selectedFilterMenu}
      onSelect={m => updateField({ selectedFilterMenu: m })}
    />
  ) : null;

  const categoryDD =
    !isEmpty(categories) && selectedTab.id === PRINTER_ROUTING_TAB_ITEM.id ? (
      <SCSelect
        title="Filter by Category"
        searchable
        options={categories}
        trackBy="id"
        customLabel={c => `${c.id}. ${c.title.en}`}
        placeholder="Select Category"
        className="w-1/3"
        value={selectedFilterCategory}
        onSelect={c => updateField({ selectedFilterCategory: c })}
      />
    ) : (
      <div className="w-1/3" />
    );

  const itemSearchEl =
    !isEmpty(items) && selectedTab.id === PRINTER_ROUTING_TAB_ITEM.id ? (
      <SCInput
        label="Filter by Item"
        placeholder="Search Item"
        className="w-1/3"
        inputClass="border border-grey rounded-lg mt-2 mb-0 h-9"
        value={itemSearchKey}
        handleChange={e =>
          updateField({
            itemSearchKey: e.target.value,
          })
        }
      />
    ) : (
      <div className="w-1/3" />
    );

  const tabEl = (() => {
    const tabs = PRINTER_ROUTING_TABS.map(t => {
      const isSelected = t.id === selectedTab.id;

      const count = (() => {
        if (t.id === PRINTER_ROUTING_TAB_CATEGORY.id) {
          return categories.length;
        }

        if (t.id === PRINTER_ROUTING_TAB_ITEM.id) {
          return items.length;
        }

        return 0;
      })();

      return (
        <div
          className={cn({
            'flex items-center justify-center px-6 py-2 h-full border-b-2 text-sm cursor-pointer gap-x-2': true,
            'text-primary border-primary font-medium': isSelected,
            'border-grey font-normal': !isSelected,
          })}
          role="presentation"
          onClick={() => onTabClicked(t)}
        >
          {t.title}
          <div
            className={cn({
              'flex items-center justify-center h-5 px-2 rounded-sm text-xs': true,
              'bg-primary-lightest text-primary': isSelected,
              'bg-grey-lighter text-grey-darker': !isSelected,
            })}
          >
            {count}
          </div>
        </div>
      );
    });

    return <div className="flex">{tabs}</div>;
  })();

  const selectionEl = (() => {
    if (isEmpty(selectedBrand))
      return (
        <div className="flex flex-1 items-center justify-center">
          <ComponentPlaceholder label="Select a brand to choose items / categories" />
        </div>
      );

    const contentEl =
      selectedTab.id === PRINTER_ROUTING_TAB_CATEGORY.id ? (
        <CategoryList />
      ) : (
        <ItemList />
      );

    return (
      <div className="flex flex-col gap-y-4">
        <div className="flex items-end justify-between">
          {tabEl}
          <SCButton
            disabled={isSaving}
            label={isSaving ? 'Saving...' : 'Save'}
            action={onSaveClicked}
          />
        </div>

        <div className="flex gap-x-4">
          {menuDD}
          {categoryDD}
          {itemSearchEl}
        </div>
        {contentEl}
      </div>
    );
  })();

  return <div className="w-full flex flex-col gap-y-4">{selectionEl}</div>;
};

AdvancedSelector.propTypes = {
  categories: PropTypes.array,
  checkedCategories: PropTypes.array,
  checkedItems: PropTypes.array,
  isSaving: PropTypes.bool,
  items: PropTypes.array,
  itemSearchKey: PropTypes.string,
  menus: PropTypes.array,
  saveBrandProfileRouting: PropTypes.func,
  selectedBrand: PropTypes.object,
  selectedFilterCategory: PropTypes.object,
  selectedFilterMenu: PropTypes.object,
  selectedProfile: PropTypes.object,
  updateField: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  categories: makeSelectProperty('categories'),
  checkedCategories: makeSelectProperty('checkedCategories'),
  checkedItems: makeSelectProperty('checkedItems'),
  isSaving: makeSelectProperty('isSaving'),
  items: makeSelectProperty('items'),
  itemSearchKey: makeSelectProperty('itemSearchKey'),
  menus: makeSelectProperty('menus'),
  selectedBrand: makeSelectProperty('selectedBrand'),
  selectedFilterCategory: makeSelectProperty('selectedFilterCategory'),
  selectedFilterMenu: makeSelectProperty('selectedFilterMenu'),
  selectedProfile: makeSelectProperty('selectedProfile'),
});

const mapDispatchToProps = dispatch => ({
  getCategories: params => dispatch(getList(params)),
  getItems: params => dispatch(getList(params)),
  saveBrandProfileRouting: params => dispatch(saveEntity(params)),
  updateField: payload => dispatch(updateEntity(payload)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(AdvancedSelector);
